import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { useTranslation } from 'react-i18next';
import { Mail, Calendar, Clock } from 'lucide-react';
import { getRecentEmails } from '../../services/google/gmail';
import { getCalendarEvents } from '../../services/google/calendar';
import { parseISO, isToday, isThisWeek, format } from 'date-fns';

export const ProductivityStats: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    unreadEmails: 0,
    todayEvents: 0,
    thisWeekEvents: 0,
    nextMeeting: null as string | null
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        
        // Charger les emails récents pour compter les non-lus
        const emails = await getRecentEmails(20);
        const unreadCount = emails.filter(email => email.labelIds?.includes('UNREAD')).length;
        
        // Charger les événements du calendrier
        const now = new Date();
        const inOneWeek = new Date();
        inOneWeek.setDate(inOneWeek.getDate() + 7);
        
        const events = await getCalendarEvents(now, inOneWeek);
        
        // Compter les événements d'aujourd'hui et de cette semaine
        const todayEvents = events.filter(event => isToday(parseISO(event.start.dateTime))).length;
        const thisWeekEvents = events.filter(event => isThisWeek(parseISO(event.start.dateTime))).length;
        
        // Trouver le prochain rendez-vous
        const futureEvents = events
          .filter(event => parseISO(event.start.dateTime) > now)
          .sort((a, b) => 
            parseISO(a.start.dateTime).getTime() - parseISO(b.start.dateTime).getTime()
          );
        
        let nextMeeting = null;
        if (futureEvents.length > 0) {
          const nextEvent = futureEvents[0];
          const startTime = format(parseISO(nextEvent.start.dateTime), 'HH:mm');
          nextMeeting = `${nextEvent.summary} (${startTime})`;
        }
        
        setStats({
          unreadEmails: unreadCount,
          todayEvents,
          thisWeekEvents,
          nextMeeting
        });
      } catch (error) {
        console.error('Error loading productivity stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadStats();
  }, []);

  const renderStatCard = (
    icon: React.ReactNode,
    title: string,
    value: string | number | null,
    color: string
  ) => (
    <Card>
      <CardContent className="flex items-center p-6">
        <div className={`rounded-full p-2 ${color} mr-4`}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {loading ? (
            <Skeleton className="h-8 w-24 mt-1" />
          ) : (
            <p className="text-2xl font-bold">{value}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      {renderStatCard(
        <Mail className="h-5 w-5 text-blue-700" />,
        t('productivity.unreadEmails', 'Emails non lus'),
        stats.unreadEmails,
        'bg-blue-100'
      )}
      
      {renderStatCard(
        <Calendar className="h-5 w-5 text-green-700" />,
        t('productivity.todayEvents', 'Rendez-vous aujourd\'hui'),
        stats.todayEvents,
        'bg-green-100'
      )}
      
      {renderStatCard(
        <Clock className="h-5 w-5 text-orange-700" />,
        t('productivity.nextMeeting', 'Prochain rendez-vous'),
        stats.nextMeeting || t('productivity.noMeetings', 'Aucun'),
        'bg-orange-100'
      )}
    </>
  );
};
